import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { leasingApi } from '@/api/leasing.api';
import { keysToCamelCase } from '@/shared/services/tools';
import type { TDashboardData } from '@/shared/types/dashboard.types';
import type {
  TLeasingFile,
  TLeasingFiles
} from '@/shared/types/leasing-files.types';
import type { TPaymentSchedule } from '@/shared/types/payment-schedule.types';

export const SCHEDULE_FETCH_LIMIT = 84;

export const getDashboardData = createAsyncThunk(
  'leasing/getDashboardData',
  async () => {
    return await leasingApi.getDashboardData();
  }
);

export const getLeasingFiles = createAsyncThunk(
  'leasing/getLeasingFiles',
  async (contractId: string) => {
    return await leasingApi.getLeasingFiles(contractId);
  }
);

export const getPaymentSchedule = createAsyncThunk(
  'leasing/getPaymentSchedule',
  async ({
    contractId,
    params
  }: {
    contractId: string;
    params?: { offset?: number; limit?: number };
  }) => {
    return await leasingApi.getPaymentSchedule(contractId, params);
  }
);

interface ILeasingState {
  dashboardData: TDashboardData | null;
  isDashboardLoading: boolean;
  leasingFiles: TLeasingFiles | null;
  paymentSchedule: TPaymentSchedule | null;
}

const initialState: ILeasingState = {
  dashboardData: null,
  isDashboardLoading: false,
  leasingFiles: null,
  paymentSchedule: null
};

const leasingSlice = createSlice({
  name: 'leasing',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardData.pending, (state) => {
        state.isDashboardLoading = true;
      })
      .addCase(getDashboardData.fulfilled, (state, action) => {
        state.isDashboardLoading = false;
        state.dashboardData = keysToCamelCase(action.payload);
      })
      .addCase(getDashboardData.rejected, (state) => {
        state.isDashboardLoading = false;
      })
      .addCase(getLeasingFiles.fulfilled, (state, action) => {
        const files = action.payload.request.Files.reduce(
          (acc, file) => {
            acc[file.file_type.code] = keysToCamelCase(file);
            return acc;
          },
          {} as Record<string, TLeasingFile>
        );
        state.leasingFiles = {
          request: {
            files,
            uuid: action.payload.request.uuid
          }
        };
      })
      .addCase(getPaymentSchedule.fulfilled, (state, action) => {
        state.paymentSchedule = keysToCamelCase(action.payload);
      });
  }
});

export default leasingSlice;
